import {
  ApplicationConfig,
  EnvironmentProviders,
  ErrorHandler,
  isDevMode,
  Provider,
  provideZoneChangeDetection
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { appRoutes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import * as Sentry from '@sentry/angular';
import { environment } from '../environments/environment';
import { provideStates, provideStore } from '@ngxs/store';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { HttpInterceptor } from './core/interceptors/http.interceptor';
import { AuthState } from './core/authentication/auth.state';
import { AppState } from './app.state';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { APP_COLLECTION, DATX_CONFIG, jsonapiAngular, setupDatx } from '@datx/jsonapi-angular';
import { Collection } from '@datx/core';
import { EditorJsonModel } from './editor/editor.json.model';
import { EditorState } from './editor/ngxs/editor/editor.state';
import { ExplanationState } from './explanation/ngxs/explanation/explanation.state';
import { SubjectJsonModel } from './subject/subject.json.model';
import { ConsumerJsonModel } from './consumer/consumer.json.model';
import { SubjectState } from './subject/ngxs/subject/subject.state';
import { FetchOptionsState } from './core/state/fetchOptions.state';
import { ExerciseJsonModel, ExerciseTypesJsonModel } from './lesson/exercise/exercise.json.model';
import { FunctionalHelpJsonModel } from './functionalHelp/functionalHelp.json.model';
import { AssetJsonModel } from './asset/assets.json.model';
import { ExplanationJsonModel } from './explanation/explanation.json.model';
import { ExplanationContentJsonModel } from './explanation/content/explanation-content.json.model';
import { GlobalMetadataJsonModel } from './global-metadata/global-metadata.json.model';
import { BookJsonModel } from './book/book.json.model';
import { ExamJsonModel } from './lesson/exam/exam.json.model';
import { TranslationService } from './core/services/translationService';
import { LearningUnitState } from './learning-unit/ngxs/learning-unit.state';
import { LearningUnitJsonModel } from './learning-unit/learning-unit.json.model';
import { LearningSectionJsonModel } from './learning-section/learning-section.json.model';
import { LearningSectionState } from './learning-section/ngxs/learning-section/learning-section.state';
import {
  LearningSectionContainerJsonModel
} from './learning-section/learning-section-container/learning-section-container.json.model';
import {
  LearningSectionContainerState
} from './learning-section/ngxs/learning-section-container/learning-section-container.state';
import { SequenceJsonModel } from './sequence/sequence.json.model';
import { SequenceState } from './sequence/ngxs/sequence/sequence.state';
import { ExplanationContentState } from './explanation/ngxs/explanation-content/explanation-content.state';
import { TestJsonModel } from './test/test.json.model';
import { TestState } from './test/ngxs/test/test.state';
import { ModuleJsonModel } from './module/module.json.model';
import { ModuleState } from './module/ngxs/module.state';
import { ConsumerState } from './consumer/ngxs/consumer.state';
import { SchoolClassJsonModel } from './core/global-enums/school-class/school-class.json.model';
import { SchoolClassState } from './core/global-enums/school-class/ngxs/school-class.state';
import {
  LearningSectionLayoutJsonModel
} from './learning-section/learning-section-layout/learning-section-layout.json.model';
import {
  LearningSectionLayoutState
} from './learning-section/ngxs/learning-section-layout/learning-section-layout.state';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco } from '@jsverse/transloco';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { cmsMatTooltipDefaults } from './app.defaults';
import { ModuleEntityState } from './module/services/module.entity.service';
import {
  ModuleLearningUnitMetadataEntityState
} from './module/services/module-learning-unit-metadata.entity.service';
import { LearningUnitEntityState } from './learning-unit/services/learning-unit.entity.service';
import { TestEntityState } from './test/services/test.entity.service';
import { ConsumerEntityState } from './consumer/services/consumer.entity.service';
import { SubjectEntityState } from './subject/services/subject.entity.service';
import { SequenceEntityState } from './sequence/services/sequence.entity.service';
import { ExamEntityState } from './lesson/exam/services/exam.entity.service';
import { ExerciseEntityState } from './lesson/exercise/services/exercise.entity.service';
import { GenericTreeEntityState } from './generic-tree/services/generic-tree.entity.service';
import { GlobalMetaDataEntityState } from './global-metadata/services/global-metadata.entity.service';
import { GlobalMetaDataTypeEntityState } from './global-metadata/services/global-metadata-type.entity.service';
import { ProductEntityState } from './product/services/product.entity.service';
import { ObjectTypeEntityState } from './core/global-enums/object-type/services/object-type.entity.service';
import { SchoolClassEntityState } from './core/global-enums/school-class/services/school-class.entity.service';
import { LearningSectionEntityState } from './learning-section/services/learning-section.entity.service';
import {
  LearningSectionContainerEntityState
} from './learning-section/learning-section-container/services/learning-section-container.entity.service';
import { ExplanationEntityState } from './explanation/services/explanation.entity.service';
import { ExplanationContentEntityState } from './explanation/content/services/explanation-content.entity.service';
import {
  LearningSectionLayoutEntityState
} from './learning-section/learning-section-layout/services/learning-section-layout.entity.service';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';

export const GlobalDialogConfig: MatDialogConfig = {
  // Custom CSS class to apply a blur effect to the background overlay
  backdropClass: 'frosted-glass',

  // Whether the dialog has a background backdrop (defaults to true)
  // hasBackdrop: true,

  // Prevents closing via backdrop click or Escape key to avoid accidental data loss
  disableClose: true,

  // Focuses the dialog container for accessibility and to prevent focus from staying on the trigger button
  autoFocus: 'dialog',
};

/**
 * Every jsonModel must be registered here!
 */
export class AppCollection extends jsonapiAngular(Collection) {
  public static override readonly types = [
    AssetJsonModel,
    EditorJsonModel,
    ConsumerJsonModel,
    ExerciseJsonModel,
    ExerciseTypesJsonModel,
    FunctionalHelpJsonModel,
    SubjectJsonModel,
    ExplanationJsonModel,
    ExplanationContentJsonModel,
    BookJsonModel,
    ExamJsonModel,
    SubjectJsonModel,
    GlobalMetadataJsonModel,
    LearningUnitJsonModel,
    LearningSectionJsonModel,
    LearningSectionContainerJsonModel,
    SequenceJsonModel,
    TestJsonModel,
    ModuleJsonModel,
    SchoolClassJsonModel,
    LearningSectionLayoutJsonModel
  ];
}

/**
 * settings for datx library (jsonApi handling).
 * Exported for easier use in TestBed.providers
 */
export const datxConfig: (Provider | EnvironmentProviders)[] = [
  { provide: APP_COLLECTION, useValue: new AppCollection() },
  {
    provide: DATX_CONFIG,
    useFactory: (httpClient: HttpClient) => {
      return setupDatx(httpClient, {
        baseUrl: environment.apiUrl + '/',
      });
    },
    deps: [HttpClient],
  }
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([HttpInterceptor])),
    TranslationService,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatMenuModule,
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 4500 } },
    provideStore([
      AppState,
      AuthState], {
      developmentMode: !environment.isProd
    }, withNgxsReduxDevtoolsPlugin({ disabled: environment.isProd })),
    ...datxConfig,
    provideAnimations(),
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: cmsMatTooltipDefaults
    },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: GlobalDialogConfig },
    provideStates([
      ConsumerEntityState,
      ConsumerState,
      EditorState,
      ExamEntityState,
      ExerciseEntityState,
      ExplanationContentState,
      ExplanationContentEntityState,
      ExplanationState,
      ExplanationEntityState,
      FetchOptionsState,
      GenericTreeEntityState,
      GlobalMetaDataEntityState,
      GlobalMetaDataTypeEntityState,
      LearningSectionContainerState,
      LearningSectionContainerEntityState,
      LearningSectionLayoutState,
      LearningSectionLayoutEntityState,
      LearningSectionState,
      LearningSectionEntityState,
      LearningUnitEntityState,
      LearningUnitState,
      ModuleEntityState,
      ModuleLearningUnitMetadataEntityState,
      ModuleState,
      ObjectTypeEntityState,
      ProductEntityState,
      SchoolClassEntityState,
      SchoolClassState,
      SequenceEntityState,
      SequenceState,
      SubjectEntityState,
      SubjectState,
      TestEntityState,
      TestState
    ]),
    provideHttpClient(),
    provideTransloco({
      config: {
        availableLangs: ['de', 'en'],
        defaultLang: 'de',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader
    })
  ]
};
